import { useRef, useState } from "react";

import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";

import { BwZoomInLine, BwZoomOutLine } from "@/components/ui/icons";
import type { LightBoxAttachment } from "@/components/ui/lightbox/light-box-attachment";
import LightBoxItem from "@/components/ui/lightbox/light-box-item";
import PdfViewer from "@/components/ui/pdf-viewer";

type LightBoxItemPdfProps<
  TAttachment extends LightBoxAttachment = LightBoxAttachment,
> = {
  item: TAttachment;
};

const LightBoxItemPdf = (props: LightBoxItemPdfProps) => {
  const { item } = props;
  const imgRef = useRef<any>(null);
  const zoomRef = useRef<any>(null);
  const [area, setArea] = useState({
    x: 0,
    y: 0,
    scale: 1,
    value: "scale3d(1,1, 1) translate3d(0px, 0px, 0)",
  });

  const onUpdate = ({ x, y, scale }: any) => {
    const { current: img } = imgRef;
    if (img) {
      const value = make3dTransformValue({ x, y, scale });
      setArea({ x: x * -1, y: y * -1, scale, value });
    }
  };

  const onZoomIn = () => {
    if (imgRef.current) {
      const data = {
        x: imgRef.current.offsetWidth / 2,
        y: imgRef.current.offsetHeight / 2,
        scale: area.scale + 0.5,
      };
      zoomRef.current.alignCenter(data);
    }
  };

  const onZoomOut = () => {
    if (imgRef.current) {
      const data = {
        x: imgRef.current.offsetWidth / 2,
        y: imgRef.current.offsetHeight / 2,
        scale: area.scale - 0.5,
      };
      zoomRef.current.alignCenter(data);
    }
  };

  const onDocumentLoadSuccess = () => {
    setTimeout(() => {
      onZoomOut();
    }, 200);
  };

  return (
    <div data-test="lightbox-item-pdf">
      <QuickPinchZoom
        ref={zoomRef}
        onUpdate={onUpdate}
        animationDuration={0}
        wheelScaleFactor={1000}
        maxZoom={50}
        minZoom={1}
        shouldInterceptWheel={(e) => {
          return e.ctrlKey || e.metaKey;
        }}
        containerProps={{
          className: "h-screen w-screen",
        }}
      >
        <div ref={imgRef} style={{ transform: area.value }}>
          <LightBoxItem item={item}>
            <PdfViewer
              file={item.contentUri ?? ""}
              onLoadSuccess={onDocumentLoadSuccess}
            />
          </LightBoxItem>
        </div>
      </QuickPinchZoom>
      <div className="absolute flex items-center justify-center w-full gap-2 bottom-6">
        <div
          className="backdrop-blur-md rounded-full w-10 h-10 cursor-pointer flex items-center justify-center border-white/[0.24] bg-bg-dark-max/[0.72]"
          onClick={onZoomOut}
        >
          <BwZoomOutLine className="w-5 h-5 text-text-light" />
        </div>
        <div
          className="backdrop-blur-md rounded-full w-10 h-10 cursor-pointer flex items-center justify-center border-white/[0.24] bg-bg-dark-max/[0.72]"
          onClick={onZoomIn}
        >
          <BwZoomInLine className="w-5 h-5 text-text-light" />
        </div>
      </div>
    </div>
  );
};

export default LightBoxItemPdf;
